// DML-shape extractor — covers INSERT / UPDATE / DELETE / MERGE.
// Slim dispatcher; per-kind work lives in `extractQueryStructure.dml.{insert,
// update,delete,merge}.ts`. Shared helpers live in `…dml.shared.ts`.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — DML.

import type { StatementInfo } from '../../sql-grammar/model';
import { buildLineIndex } from '../../sql-grammar/document/lineIndex';
import { computeCteBindings } from '../scope/cteScope';
import type { ParsedBuffer, QueryStructure } from '../types';

import type { SelectExtractCtx } from './extractQueryStructure.select';
import { extractInsertShape } from './extractQueryStructure.dml.insert';
import { extractUpdateShape } from './extractQueryStructure.dml.update';
import { extractDeleteShape } from './extractQueryStructure.dml.delete';
import { extractMergeStructure } from './extractQueryStructure.dml.merge';

export function extractDmlStructure(
  buffer: ParsedBuffer,
  statementInfo: StatementInfo,
): QueryStructure {
  const { source, options } = buffer.parsedDocument;
  const lineIdx = buildLineIndex(source);
  const ctx: SelectExtractCtx = { buffer, source, options, lineIdx };
  const ctes = computeCteBindings(buffer, statementInfo.id);
  const start = statementInfo.range.startOffset;
  const end = statementInfo.range.endOffset;

  const base: QueryStructure = {
    kind: statementInfo.kind as QueryStructure['kind'],
    statementRange: statementInfo.range,
    ...(ctes.length ? { ctes } : {}),
  };

  switch (statementInfo.kind) {
    case 'insert': return { ...base, ...extractInsertShape(ctx, start, end) };
    case 'update': return { ...base, ...extractUpdateShape(ctx, start, end) };
    case 'delete': return { ...base, ...extractDeleteShape(ctx, start, end) };
    case 'merge':  return { ...base, ...extractMergeStructure(ctx, start, end) };
    default: return base;
  }
}

// Re-export shared helpers so the merge sub-extractor (and any future caller)
// can reach them via the public dml entry.
export {
  parseAssignmentsRange,
  readDmlTarget,
  type MutableQueryStructure,
} from './extractQueryStructure.dml.shared';
